import { AiSurface } from '@/components/AiSurface';
import { featureFlags } from '@/featureFlags';
import { VoiceInputButton } from '@/features/voice-input/components/VoiceInputButton';
import { useT } from '@/i18n';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { useAui, useAuiState } from '@assistant-ui/react';
import type { AiAttachmentUpload } from '@restaurant/api-client';
import { AiAssetUrlPrefix } from '@restaurant/api-contracts';
import { Button, Icon, Link, Preloader, f7 } from 'framework7-react';
import { useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { uploadAttachment } from '../api/assistant-api';

const ACCEPTED_IMAGES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_ATTACHMENTS = 5;

export function Composer() {
  const t = useT().aiAssistant;
  const aui = useAui();
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const isRunning = useAuiState((s) => s.thread.isRunning);
  const [text, setText] = useState('');
  const [attachments, setAttachments] = useState<AiAttachmentUpload[]>([]);
  const [uploading, setUploading] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const addFiles = async (files: File[]) => {
    const images = files
      .filter((file) => ACCEPTED_IMAGES.includes(file.type))
      .slice(0, MAX_ATTACHMENTS - attachments.length);
    if (images.length === 0 || !restaurantId) return;
    setUploading((n) => n + images.length);
    await Promise.all(
      images.map(async (file) => {
        try {
          const uploaded = await uploadAttachment(restaurantId, file);
          setAttachments((current) => [...current, uploaded]);
        } catch (error) {
          f7.toast.show({
            text: error instanceof Error ? error.message : t.uploadFailed,
            closeTimeout: 3000,
            position: 'bottom',
          });
        } finally {
          setUploading((n) => n - 1);
        }
      }),
    );
  };

  const canSend =
    !isRunning && uploading === 0 && (text.trim().length > 0 || attachments.length > 0);

  const send = () => {
    if (!canSend) return;
    aui.thread.append({
      role: 'user',
      content: [
        ...(text.trim() ? [{ type: 'text' as const, text: text.trim() }] : []),
        ...attachments.map((attachment) => ({
          type: 'file' as const,
          data: `${AiAssetUrlPrefix}${attachment.id}`,
          mimeType: attachment.mimeType,
          filename: attachment.fileName,
          sourceType: 'id' as const,
        })),
      ],
    });
    setText('');
    setAttachments([]);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      send();
    }
  };

  const onPaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const files = Array.from(event.clipboardData.files);
    if (files.length === 0) return;
    event.preventDefault();
    void addFiles(files);
  };

  return (
    <div className="ai-composer">
      {(attachments.length > 0 || uploading > 0) && (
        <div className="ai-composer-attachments">
          {attachments.map((attachment) => (
            <span key={attachment.id} className="ai-file-chip">
              <Icon f7="photo" size={14} />
              {attachment.fileName}
              <Link
                href={false}
                iconF7="xmark_circle_fill"
                iconSize={16}
                aria-label={t.removeAttachment}
                data-testid={`ai-composer-remove-${attachment.id}`}
                onClick={() =>
                  setAttachments((current) => current.filter((a) => a.id !== attachment.id))
                }
              />
            </span>
          ))}
          {uploading > 0 && (
            <span className="ai-file-chip">
              <Preloader size={14} />
              {t.uploading}
            </span>
          )}
        </div>
      )}
      <AiSurface busy={isRunning} className="ai-composer-surface" data-testid="ai-composer">
        <div className="ai-composer-row">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_IMAGES.join(',')}
            multiple
            hidden
            data-testid="ai-composer-file-input"
            onChange={(event) => {
              void addFiles(Array.from(event.target.files ?? []));
              event.target.value = '';
            }}
          />
          <Link
            href={false}
            className="ai-composer-icon"
            iconF7="paperclip"
            aria-label={t.attachImage}
            data-testid="ai-composer-attach"
            onClick={() => fileInputRef.current?.click()}
          />
          <textarea
            className="ai-composer-input"
            rows={1}
            value={text}
            placeholder={t.inputPlaceholder}
            data-testid="ai-composer-input"
            onChange={(event) => setText(event.target.value)}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
          />
          {featureFlags.voiceInput && (
            <VoiceInputButton value={text} onChange={setText} data-testid="ai-composer-voice" />
          )}
          {isRunning ? (
            <Button
              round
              fill
              color="red"
              className="ai-composer-send"
              aria-label={t.stop}
              data-testid="ai-composer-stop"
              onClick={() => aui.thread.cancelRun()}
            >
              <Icon f7="stop_fill" size={16} />
            </Button>
          ) : (
            <Button
              round
              fill
              className="ai-composer-send"
              disabled={!canSend}
              aria-label={t.send}
              data-testid="ai-composer-send"
              onClick={send}
            >
              <Icon f7="arrow_up" size={16} />
            </Button>
          )}
        </div>
      </AiSurface>
    </div>
  );
}
